import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { createEpic, errorMessage, getView, setEpicStep, updateView } from "./api";
import { LabelChips, StepLabels } from "./components";
import EpicRow from "./EpicRow";
import { NewEpicModal, ViewFormModal } from "./forms";
import "./ProjectViews.css";

/** One view: its epics, live from Jira, against the view's steps. */
export default function ProjectViewPage({ onReady }) {
  const [theme] = useDarkMode();
  const { viewId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // epic key → true while its step saves
  const [modal, setModal] = useState(null); // "edit" | "epic"

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getView(viewId));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
      onReady?.();
    }
  }, [viewId, onReady]);

  useEffect(() => {
    load();
  }, [load]);

  const view = data?.view;
  const epics = useMemo(() => data?.epics || [], [data]);

  /** Applies a change Jira already accepted (title, description, status) to one row. */
  const patchEpic = (key, changes) => {
    setData((d) => ({ ...d, epics: d.epics.map((e) => (e.key === key ? { ...e, ...changes } : e)) }));
  };

  const changeStep = async (epic, step) => {
    const previous = epic.step;
    const next = step || null;
    setSaving((s) => ({ ...s, [epic.key]: true }));
    setData((d) => ({ ...d, epics: d.epics.map((e) => (e.key === epic.key ? { ...e, step: next } : e)) }));
    try {
      await setEpicStep(epic.key, step);
      toast.success(next ? `${epic.key} moved to ${next}` : `${epic.key} step cleared`);
    } catch (err) {
      setData((d) => ({ ...d, epics: d.epics.map((e) => (e.key === epic.key ? { ...e, step: previous } : e)) }));
      toast.error(`${epic.key}: ${errorMessage(err)}`);
    } finally {
      setSaving((s) => ({ ...s, [epic.key]: false }));
    }
  };

  const saveView = async (input) => {
    try {
      await updateView(viewId, input);
      setModal(null);
      toast.success("View saved");
      load();
    } catch (err) {
      throw new Error(errorMessage(err));
    }
  };

  const addEpic = async (input) => {
    try {
      const { epic } = await createEpic(viewId, input);
      setModal(null);
      toast.success(`Created ${epic.key} in Jira`);
      load();
    } catch (err) {
      throw new Error(errorMessage(err));
    }
  };

  return (
    <div className="pv-page" data-theme={theme}>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <Link to="/project-views" className="pv-back">
        ← All views
      </Link>

      {error ? <div className="pv-error">{error}</div> : null}
      {!view && loading ? <div className="pv-empty">Loading from Jira…</div> : null}

      {view ? (
        <>
          <header className="pv-head">
            <div>
              <h1>{view.name}</h1>
              {view.description ? <p className="pv-sub">{view.description}</p> : null}
              <div className="pv-head-meta">
                <LabelChips labels={view.labels} />
                <span>
                  {epics.length} {epics.length === 1 ? "epic" : "epics"} in {view.projectKey}
                  {view.labelMatch === "any" ? " with any of these labels" : ""}
                </span>
                {data.jiraUrl ? (
                  <a href={data.jiraUrl} target="_blank" rel="noreferrer">
                    Open in Jira
                  </a>
                ) : null}
              </div>
            </div>
            <div className="pv-head-actions">
              <button type="button" className="pv-btn" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="pv-btn" onClick={() => setModal("edit")}>
                Edit view
              </button>
              <button type="button" className="pv-btn pv-btn--primary" onClick={() => setModal("epic")}>
                New project
              </button>
            </div>
          </header>

          {epics.length === 0 ? (
            <div className="pv-empty">
              No epics in {view.projectKey} carry {view.labels.length === 1 ? "this label" : "these labels"} yet.
              Create one with New project, or add the label to an epic in Jira.
            </div>
          ) : (
            <table className="pv-table">
              <colgroup>
                <col className="pv-col-project" />
                <col className="pv-col-steps" />
                <col className="pv-col-jira" />
              </colgroup>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>
                    <StepLabels steps={view.steps} />
                  </th>
                  <th>In Jira</th>
                </tr>
              </thead>
              <tbody>
                {epics.map((epic) => (
                  <EpicRow
                    key={epic.key}
                    epic={epic}
                    steps={view.steps}
                    savingStep={Boolean(saving[epic.key])}
                    onStepChange={(step) => changeStep(epic, step)}
                    onEpicChange={(changes) => patchEpic(epic.key, changes)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}

      {modal === "edit" && view ? (
        <ViewFormModal view={view} onClose={() => setModal(null)} onSave={saveView} />
      ) : null}
      {modal === "epic" && view ? (
        <NewEpicModal view={view} onClose={() => setModal(null)} onCreate={addEpic} />
      ) : null}
    </div>
  );
}
